type IframelyResponse = {
  html?: string;
  meta?: {
    [key: string]: string;
  };
};

type IframelyEmbed = {
  html: string;
  width: number;
  height: number;
  medium?: string;
};

async function queryIframely(
  url: string,
  maxWidth: number,
  maxHeight: number,
): Promise<IframelyResponse> {
  const apiKey = process.env.NEXT_PUBLIC_IFRAMELY_KEY;

  const response = await fetch(
    `https://iframely.net/api/iframely` +
      `?url=${encodeURIComponent(url)}&key=${apiKey}` +
      `&iframe=1&omit_script=1&omit_css=1` +
      `&maxwidth=${maxWidth}&maxheight=${maxHeight}`,
  );

  return response.json() as Promise<IframelyResponse>;
}

function fitToBox(width: number, height: number, maxWidth: number, maxHeight: number) {
  if (width <= maxWidth && height <= maxHeight) {
    return [width, height];
  }

  const aspectRatio = width / height;
  const maxAspectRatio = maxWidth / maxHeight;

  if (aspectRatio > maxAspectRatio) {
    return [maxWidth, Math.floor(maxWidth / aspectRatio)];
  }

  return [Math.floor(maxHeight * aspectRatio), maxHeight];
}

function extractEmbedData(
  resp: IframelyResponse,
  maxWidth: number,
  maxHeight: number,
): IframelyEmbed | null {
  const iframe = (resp.html || "").match(/<iframe[^>]*>.*?<\/iframe>/i)?.[0];
  if (!iframe) {
    return null;
  }

  const sWidth = iframe.match(/\swidth=["']?(\d+)["']?/i)?.[1] || "";
  const sHeight = iframe.match(/\sheight=["']?(\d+)["']?/i)?.[1] || "";

  const [width, height] = fitToBox(
    parseInt(sWidth, 10) || maxWidth,
    parseInt(sHeight, 10) || maxHeight,
    maxWidth,
    maxHeight,
  );

  return { html: iframe, width, height, medium: resp.meta?.medium };
}

export async function fetchEmbed(
  url: string,
  maxWidth: number,
  maxHeight: number,
): Promise<IframelyEmbed> {
  try {
    const resp = await queryIframely(url, maxWidth, maxHeight);
    const embedData = extractEmbedData(resp, maxWidth, maxHeight);

    if (embedData) {
      return embedData;
    }

    console.error("Failed to load embed, unexpected response object", resp);
    throw new Error("Failed to load embed");
  } catch (e) {
    console.error("Failed to fetch embed", e);
    throw new Error("Failed to fetch embed");
  }
}
